import type { ReactElement } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { useMeQuery } from '@/api/authApi';
import { Layout } from '@/components/Layout';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { ArticlesPage } from '@/pages/ArticlesPage';
import { AuditLogPage } from '@/pages/AuditLogPage';
import { CafeApplicationDetailPage } from '@/pages/CafeApplicationDetailPage';
import { CafeApplicationsPage } from '@/pages/CafeApplicationsPage';
import { CafeStaffSearchLogsPage } from '@/pages/CafeStaffSearchLogsPage';
import { CategoriesPage } from '@/pages/CategoriesPage';
import { CitiesPage } from '@/pages/CitiesPage';
import { CommentsPage } from '@/pages/CommentsPage';
import { CoursesPage } from '@/pages/CoursesPage';
import { CuisineTypesPage } from '@/pages/CuisineTypesPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { EventsPage } from '@/pages/EventsPage';
import { IngredientGroupsPage } from '@/pages/IngredientGroupsPage';
import { IngredientsPage } from '@/pages/IngredientsPage';
import { LoginPage } from '@/pages/LoginPage';
import { MenuItemTypesPage } from '@/pages/MenuItemTypesPage';
import { ModerationDetailPage } from '@/pages/ModerationDetailPage';
import { ModerationPage } from '@/pages/ModerationPage';
import { PaymentDetailPage } from '@/pages/PaymentDetailPage';
import { PaymentsPage } from '@/pages/PaymentsPage';
import { PiiConsentTextsPage } from '@/pages/PiiConsentTextsPage';
import { ProfessionalApplicationDetailPage } from '@/pages/ProfessionalApplicationDetailPage';
import { ProfessionalApplicationsPage } from '@/pages/ProfessionalApplicationsPage';
import { RecipesPage } from '@/pages/RecipesPage';
import { SpecializationsPage } from '@/pages/SpecializationsPage';
import { StreamsPage } from '@/pages/StreamsPage';
import { SubscriptionDetailPage } from '@/pages/SubscriptionDetailPage';
import { SubscriptionsPage } from '@/pages/SubscriptionsPage';
import { TariffsPage } from '@/pages/TariffsPage';
import { VacancyComplaintDetailPage } from '@/pages/VacancyComplaintDetailPage';
import { VacancyComplaintsPage } from '@/pages/VacancyComplaintsPage';

function App(): ReactElement {
    // Запускаем /admin-auth/me один раз при маунте — extraReducers в authSlice
    // подхватят результат и проставят admin/isAuthenticated/isLoading.
    useMeQuery();

    return (
        <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route element={<ProtectedRoute />}>
                <Route element={<Layout />}>
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/moderation" element={<ModerationPage />} />
                    <Route path="/moderation/:type/:id" element={<ModerationDetailPage />} />
                    <Route path="/recipes" element={<RecipesPage />} />
                    <Route path="/articles" element={<ArticlesPage />} />
                    <Route path="/courses" element={<CoursesPage />} />
                    <Route path="/streams" element={<StreamsPage />} />
                    <Route path="/events" element={<EventsPage />} />
                    <Route path="/comments" element={<CommentsPage />} />
                    <Route path="/categories" element={<CategoriesPage />} />
                    <Route path="/ingredient-groups" element={<IngredientGroupsPage />} />
                    <Route path="/ingredients" element={<IngredientsPage />} />
                    <Route path="/cuisine-types" element={<CuisineTypesPage />} />
                    <Route path="/cities" element={<CitiesPage />} />
                    <Route path="/tariffs" element={<TariffsPage />} />
                    <Route path="/subscriptions" element={<SubscriptionsPage />} />
                    <Route path="/subscriptions/:id" element={<SubscriptionDetailPage />} />
                    <Route path="/payments" element={<PaymentsPage />} />
                    <Route path="/payments/:id" element={<PaymentDetailPage />} />
                    <Route
                        path="/professional-applications"
                        element={<ProfessionalApplicationsPage />}
                    />
                    <Route
                        path="/professional-applications/:id"
                        element={<ProfessionalApplicationDetailPage />}
                    />
                    <Route path="/cafe-applications" element={<CafeApplicationsPage />} />
                    <Route
                        path="/cafe-applications/:id"
                        element={<CafeApplicationDetailPage />}
                    />
                    <Route path="/vacancy-complaints" element={<VacancyComplaintsPage />} />
                    <Route
                        path="/vacancy-complaints/:id"
                        element={<VacancyComplaintDetailPage />}
                    />
                    <Route path="/specializations" element={<SpecializationsPage />} />
                    <Route path="/menu-item-types" element={<MenuItemTypesPage />} />
                    <Route
                        path="/pii-consent-texts"
                        element={<PiiConsentTextsPage />}
                    />
                    <Route
                        path="/cafe-staff-search-logs"
                        element={<CafeStaffSearchLogsPage />}
                    />
                    <Route path="/audit-log" element={<AuditLogPage />} />
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Route>
            </Route>
        </Routes>
    );
}

export default App;
